import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

export const fetchSignalsForUser = createAsyncThunk('recruitercontacts/fetchsignalsforuser', async (payload, thunkAPI) => {
    try {
        const token = localStorage.getItem('token');

        const response = await api.post(
            `/api/recruitercontacts/getcachedsignals`,
            payload,
            {
                headers: {
                    "auth-token": token
                }
            }
        );

        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(
            error.response?.data?.message ||
            error.message ||
            'Failed to fetch signals'
        );
    }
}
);

export const fetchSignalsByCompany = createAsyncThunk('recruitercontacts/fetchsignalsbycompany', async ({ companyName, preferredDepartments }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const response = await api.post(
            `/api/recruitercontacts/fetchsignalsbycompany`,
            { companyName, preferredDepartments },
            {
                headers: {
                    "auth-token": token
                }
            }
        );

        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(
            error.response?.data?.message ||
            error.message ||
            'Failed to fetch signals'
        );
    }
}
);

// Derive the cache key + request params from the thunk arg.
// Accepts a plain string (legacy: org id) OR { key, name } where `name` is sent
// as a ?name= hint so the backend can match contacts by company name (used for
// jobs-feed / hiring-signal companies that have no Apollo org id).
export const contactArgKey = (arg) => (typeof arg === 'string' ? arg : arg?.key);

export const fetchAllContactsByCompanyId = createAsyncThunk('contacts/fetchAllContactsByCompanyId', async (arg, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const key = contactArgKey(arg);
        const name = typeof arg === 'string' ? null : arg?.name;

        const url = `/api/othercontacts/fetchcontactsbycompany/${encodeURIComponent(key)}`
            + (name ? `?name=${encodeURIComponent(name)}` : '');

        const response = await api.get(url, {
            headers: {
                "auth-token": token
            }
        });

        return {
            companyId: key,
            data: response.data
        };
    } catch (error) {
        return thunkAPI.rejectWithValue(
            error.response?.data?.message ||
            error.message ||
            "Failed to fetch company contacts"
        );
    }
}
);

// Batch variant of fetchAllContactsByCompanyId — resolves contacts for many
// pipeline companies in ONE request instead of one request per company.
// arg: { companies: [{ key, name }] }
export const fetchContactsForCompanies = createAsyncThunk('contacts/fetchContactsForCompanies', async ({ companies }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const response = await api.post('/api/othercontacts/fetchcontactsbycompany-batch',
            { companies },
            { headers: { "auth-token": token } }
        );
        return response.data; // { results: { [key]: { recruiterContacts, otherContacts, allContacts } } }
    } catch (error) {
        return thunkAPI.rejectWithValue(
            error.response?.data?.message ||
            error.message ||
            "Failed to fetch company contacts"
        );
    }
});

export const fetchContactCompanyIndex = createAsyncThunk('contacts/fetchContactCompanyIndex', async (_, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const response = await api.get('/api/othercontacts/contact-company-index', {
            headers: { "auth-token": token }
        });
        return response.data; // { names: [...], orgIds: [...] }
    } catch (error) {
        return thunkAPI.rejectWithValue(
            error.response?.data?.message || error.message || "Failed to fetch contact index"
        );
    }
});

export const fetchReferralScore = createAsyncThunk('contacts/fetchReferralScore', async ({ companyName, preferredDepartment }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const response = await api.post("/api/othercontacts/askreferralpossibility",
            { companyName, preferredDepartment },
            {
                headers: {
                    "auth-token": token
                }
            }
        )

        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(
            error.response?.data?.message ||
            error.message ||
            "Failed to fetch company contacts"
        );
    }
})

export const saveRecruiterContacts = createAsyncThunk('contacts/saveRecruiterContacts', async ({ jobId, companyId, organizationId, recruiterData }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        console.log('💾 Saving recruiter contacts to backend:', { jobId, companyId, organizationId });

        const response = await api.post("/api/othercontacts/saverecruitercontacts",
            { jobId, companyId, organizationId, recruiterData },
            {
                headers: {
                    "auth-token": token
                }
            }
        )

        return {
            organizationId,
            data: response.data
        };
    } catch (error) {
        console.error('❌ Error saving recruiter contacts:', error);
        return thunkAPI.rejectWithValue(
            error.response?.data?.message ||
            error.message ||
            "Failed to save recruiter contacts"
        );
    }
})

export const fetchCachedRecruiters = createAsyncThunk('contacts/fetchCachedRecruiters', async ({ organizationId, companyName }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        /*
         * `organizationId` comes from TheirStack and no SEC filer has one, so
         * the public-company board can only identify an employer by name. The
         * endpoint already falls back to matching organization_name when the id
         * finds nothing — it just needed the name passing through as `?name=`.
         */
        const key = organizationId || encodeURIComponent(companyName || '');
        console.log('🔍 Fetching cached recruiters for:', organizationId || companyName);

        const response = await api.get(`/api/othercontacts/fetchcontactsbycompany/${key}`,
            {
                params: companyName ? { name: companyName } : undefined,
                headers: {
                    "auth-token": token
                }
            }
        )

        const { recruiterContacts, otherContacts } = response.data;
        
        // Helper function to map database fields to API format
        const mapDbToApiFormat = (contact) => ({
            name: contact.name,
            title: contact.title,
            profile_url: contact.linkedin_url || contact.profile_url,
            profile_photo: contact.photo_url || contact.profile_photo,
            photo_url: contact.photo_url || contact.profile_photo,
            image: contact.photo_url || contact.profile_photo,
            email: contact.email,
            location: contact.location,
            confidence_score: contact.confidence_score || 90,
            referral_probability: contact.referral_probability,
            // Why this contact ranks where it does (seniority / tenure / shared
            // background), scored server-side against the signed-in user.
            relevance: contact.relevance || null,

            // Full data for rich display
            _fullData: {
                data_source: contact.data_source || 'Cached',
                followers: contact.followers,
                contact_availability: {
                    work_email: !!contact.email,
                    personal_email: false,
                    phone: false
                },
                overall_experience_years: contact.overall_experience_years,
                company_experience_years: contact.company_experience_years,
                summary: contact.summary,
                skills: contact.skills || [],
                education: contact.education || [],
                certifications: contact.certifications || [],
                languages: contact.languages || []
            }
        });
        
        // Format cached contacts back into the same structure as API response
        const hiringManagers = recruiterContacts?.filter(c => c.predicted_role === 'hiring_manager').map(mapDbToApiFormat) || [];
        const recruiters = recruiterContacts?.filter(c => c.predicted_role === 'recruiter').map(mapDbToApiFormat) || [];
        
        const formattedData = {
            hiring_manager: {
                best_guesses: hiringManagers,
                is_exact_match: true
            },
            recruiters: {
                best_guesses: recruiters,
                is_exact_match: true
            }
        };

        console.log('✅ Found cached recruiters:', {
            hiringManagers: hiringManagers.length,
            recruiters: recruiters.length
        });

        return formattedData;
    } catch (error) {
        console.log('⚠️ No cached recruiters found, will fetch from API');
        return thunkAPI.rejectWithValue(
            error.response?.data?.message ||
            error.message ||
            "No cached recruiters found"
        );
    }
})

export const generateReferralStrategy = createAsyncThunk(
    'contacts/generateReferralStrategy',
    async ({ jobId, organizationId, jobDetails, userProfile, allContacts }, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const { clientDataCache, getCurrentUserIdFromStorage } = require('../../utils/clientDataCache');

            const resolvedUserId =
                userProfile?._id ||
                userProfile?.id ||
                getCurrentUserIdFromStorage();

            if (resolvedUserId && organizationId) {
                const cached = clientDataCache.readReferralStrategy(resolvedUserId, organizationId);
                if (cached?.strategy) {
                    return {
                        organizationId,
                        strategy: cached.strategy,
                        source: cached.source || 'cache',
                        companyName: cached.companyName,
                        jobPosition: cached.jobPosition
                    };
                }
            }

            console.log('🤖 Generating referral strategy with DeepSeek for:', organizationId);

            const response = await api.post(
                `/api/recruitercontacts/generate-account-strategy`,
                { 
                    jobId, 
                    organizationId,
                    jobDetails,
                    userProfile,
                    allContacts
                },
                {
                    headers: {
                        "auth-token": token
                    }
                }
            );

            const payload = {
                organizationId,
                strategy: response.data.strategy,
                source: response.data.source,
                companyName: response.data.companyName,
                jobPosition: response.data.jobPosition
            };

            if (resolvedUserId && organizationId) {
                clientDataCache.writeReferralStrategy(resolvedUserId, organizationId, {
                    strategy: payload.strategy,
                    source: payload.source,
                    companyName: payload.companyName,
                    jobPosition: payload.jobPosition
                });
            }

            return payload;
        } catch (error) {
            console.error('❌ Failed to generate referral strategy:', error);
            return thunkAPI.rejectWithValue(
                error.response?.data?.message ||
                error.message ||
                "Failed to generate referral strategy"
            );
        }
    }
);

// export const startContactSearch = createAsyncThunk("contacts/startContactSearch", async ({payload}, thunkAPI) => {
//     try {
//         const token = localStorage.getItem("token");

//         const response = await api.post(
//             `/api/othercontacts/fetchcontactsbycompany/${companyId}`,
//             {
//                 headers: {
//                     "auth-token": token
//                 }
//             }
//         );
//     } catch (error) {

//     }
// })

const contactsSlice = createSlice({
    name: "contacts",
    initialState: {
        contacts: [],
        companycontacts: [],
        recruiterContacts: [],
        otherContacts: [],
        contactsByCompanyId: {},
        referralStrategies: {}, // 🆕 Store strategies by organizationId
        isSearching: false,
        referralScore: null,
        // Index of companies that have any stored contacts (lowercased names + org ids).
        // Used by the networking pipeline to pre-filter which jobs/signals to show.
        contactCompanyIndex: { names: [], orgIds: [] },
        contactCompanyIndexLoaded: false,
        loading: false,
        error: null
    },
    reducers: {
        startRealtimeSearch: (state) => {
            state.isSearching = true;
        },
        setSearchComplete: (state) => {
            state.isSearching = false;
        },
        setCompanyLoading: (state, action) => {
            const { organizationId } = action.payload;
            state.contactsByCompanyId[organizationId] = {
                recruiterContacts: [],
                otherContacts: [],
                loading: true,
                error: null,
                analysisComplete: false,
                preAnalyzedData: null,
                ...state.contactsByCompanyId[organizationId], // Preserve existing contacts if any
            };
        },
        // ACTION: Triggered by 'contacts-ready' event. Populates a company's contacts and stops loading only if analysis is done
        updateContactsForCompany: (state, action) => {
            const { organizationId, recruiterContacts, otherContacts } = action.payload;
            const prev = state.contactsByCompanyId[organizationId] || {};
            state.contactsByCompanyId[organizationId] = {
                recruiterContacts: recruiterContacts || [],
                otherContacts: otherContacts || [],
                loading: prev.analysisComplete ? false : (prev.loading ?? false), // Keep loading if analysis not done
                error: null,
                analysisComplete: prev.analysisComplete ?? false,
                preAnalyzedData: prev.preAnalyzedData ?? null
            };
        },
        // ACTION: Triggered by 'search-error' event. Sets an error message for a specific company card.
        setCompanyError: (state, action) => {
            const { organizationId, message } = action.payload;
            state.contactsByCompanyId[organizationId] = {
                ...state.contactsByCompanyId[organizationId],
                loading: false,
                error: message || 'An unknown error occurred.',
                analysisComplete: false
            };
        },
        // ACTION: mark analysis complete for an organization (called when hidden analysis finishes)
        setCompanyAnalysisComplete: (state, action) => {
            const { organizationId, analyzedData } = action.payload;
            const prev = state.contactsByCompanyId[organizationId] || {};
            state.contactsByCompanyId[organizationId] = {
                ...prev,
                preAnalyzedData: analyzedData || {},
                analysisComplete: true,
                loading: false,
                error: null
            };
        },
        // ACTION: set pre-analyzed data without changing loading (optional)
        setPreAnalyzedData: (state, action) => {
            const { organizationId, analyzedData } = action.payload;
            const prev = state.contactsByCompanyId[organizationId] || {};
            state.contactsByCompanyId[organizationId] = {
                ...prev,
                preAnalyzedData: analyzedData || {}
            };
        },
        // Clear pending state (used when starting a new job fetch so previous 'ready' UI is replaced)
        clearCompanyPending: (state, action) => {
            const { organizationId } = action.payload;
            if (state.contactsByCompanyId[organizationId]) {
                delete state.contactsByCompanyId[organizationId];
            }
        },
        // Set demo contacts for onboarding
        setDemoContacts: (state, action) => {
            state.contactsByCompanyId = action.payload;
            state.loading = false;
            state.error = null;
        },
        // Set referral strategy for onboarding
        setReferralStrategy: (state, action) => {
            const { organizationId, data, loading, error } = action.payload;
            state.referralStrategies[organizationId] = {
                data,
                loading: loading || false,
                error: error || null
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchSignalsForUser
            .addCase(fetchSignalsForUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSignalsForUser.fulfilled, (state, action) => {
                state.loading = false;
                state.contacts = action.payload;
            })
            .addCase(fetchSignalsForUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // fetchSignalsByCompany
            .addCase(fetchSignalsByCompany.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSignalsByCompany.fulfilled, (state, action) => {
                state.loading = false;
                state.companycontacts = action.payload;
            })
            .addCase(fetchSignalsByCompany.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // fetchContactsForCompanies (batch)
            .addCase(fetchContactsForCompanies.pending, (state, action) => {
                (action.meta.arg?.companies || []).forEach(({ key }) => {
                    if (!key) return;
                    const existingState = state.contactsByCompanyId[key] || {};
                    state.contactsByCompanyId[key] = {
                        recruiterContacts: existingState.recruiterContacts || [],
                        otherContacts: existingState.otherContacts || [],
                        ...existingState,
                        loading: true,
                        error: null,
                    };
                });
            })
            .addCase(fetchContactsForCompanies.fulfilled, (state, action) => {
                const results = action.payload?.results || {};
                (action.meta.arg?.companies || []).forEach(({ key }) => {
                    if (!key) return;
                    const data = results[key];
                    state.contactsByCompanyId[key] = {
                        recruiterContacts: data?.recruiterContacts || [],
                        otherContacts: data?.otherContacts || [],
                        loading: false,
                        error: data ? null : 'Failed to fetch contacts',
                    };
                });
            })
            .addCase(fetchContactsForCompanies.rejected, (state, action) => {
                const errorMessage = action.payload || 'Failed to fetch contacts';
                (action.meta.arg?.companies || []).forEach(({ key }) => {
                    if (!key) return;
                    const existingState = state.contactsByCompanyId[key] || {};
                    state.contactsByCompanyId[key] = {
                        recruiterContacts: existingState.recruiterContacts || [],
                        otherContacts: existingState.otherContacts || [],
                        ...existingState,
                        loading: false,
                        error: errorMessage,
                    };
                });
            })

            // fetchContactCompanyIndex
            .addCase(fetchContactCompanyIndex.fulfilled, (state, action) => {
                state.contactCompanyIndex = {
                    names: action.payload?.names || [],
                    orgIds: action.payload?.orgIds || [],
                };
                state.contactCompanyIndexLoaded = true;
            })
            .addCase(fetchContactCompanyIndex.rejected, (state) => {
                state.contactCompanyIndexLoaded = true;
            })

            // fetchAllContactsByCompanyId
            .addCase(fetchAllContactsByCompanyId.pending, (state, action) => {
                const organizationId = contactArgKey(action.meta.arg);
                const existingState = state.contactsByCompanyId[organizationId] || {};
                state.contactsByCompanyId[organizationId] = {
                    recruiterContacts: existingState.recruiterContacts || [],
                    otherContacts: existingState.otherContacts || [],
                    ...existingState,
                    loading: true,
                    error: null,
                };
            })
            .addCase(fetchAllContactsByCompanyId.fulfilled, (state, action) => {
                // Correctly destructure the payload
                const { companyId, data } = action.payload;
                const organizationId = companyId;

                state.contactsByCompanyId[organizationId] = {
                    recruiterContacts: data.recruiterContacts || [],
                    otherContacts: data.otherContacts || [],
                    loading: false,
                    error: null,
                };
            })
            .addCase(fetchAllContactsByCompanyId.rejected, (state, action) => {
                // action.payload is a plain error string — read companyId from meta.arg
                const organizationId = contactArgKey(action.meta.arg);
                const errorMessage = action.payload || 'Failed to fetch contacts';

                const existingState = state.contactsByCompanyId[organizationId] || {};
                state.contactsByCompanyId[organizationId] = {
                    recruiterContacts: existingState.recruiterContacts || [],
                    otherContacts: existingState.otherContacts || [],
                    ...existingState,
                    loading: false,
                    error: errorMessage,
                };
            })

            // Fetch contacts for referral
            .addCase(fetchReferralScore.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchReferralScore.fulfilled, (state, action) => {
                state.referralScore = action.payload.referralScore;
                state.loading = false;
            })
            .addCase(fetchReferralScore.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Save recruiter contacts
            .addCase(saveRecruiterContacts.pending, (state, action) => {
                const organizationId = action.meta.arg.organizationId;
                if (organizationId) {
                    state.contactsByCompanyId[organizationId] = {
                        ...state.contactsByCompanyId[organizationId],
                        loading: true,
                        error: null,
                        analysisComplete: false, // reset analysis flag; analysis will happen in hidden modal
                    };
                }
            })
            .addCase(saveRecruiterContacts.fulfilled, (state, action) => {
                const { organizationId, data } = action.payload;
                
                console.log('✅ Recruiter contacts saved successfully to backend');
                
                const prev = state.contactsByCompanyId[organizationId] || {};
                
                // keep loading true if analysis not complete yet (analysisComplete will be set by analysis callback)
                const stillLoading = !(prev.analysisComplete === true);

                state.contactsByCompanyId[organizationId] = {
                    recruiterContacts: data.recruiterContacts || [],
                    otherContacts: data.otherContacts || [],
                    loading: stillLoading,
                    error: null,
                    analysisComplete: prev.analysisComplete ?? false,
                    preAnalyzedData: prev.preAnalyzedData ?? null
                };
            })
            .addCase(saveRecruiterContacts.rejected, (state, action) => {
                const organizationId = action.meta.arg.organizationId;
                if (organizationId && state.contactsByCompanyId[organizationId]) {
                    state.contactsByCompanyId[organizationId] = {
                        ...state.contactsByCompanyId[organizationId],
                        loading: false,
                        error: action.payload,
                        analysisComplete: false
                    };
                }
            })

            // 🆕 Generate referral strategy
            .addCase(generateReferralStrategy.pending, (state, action) => {
                const organizationId = action.meta.arg.organizationId;
                state.referralStrategies[organizationId] = {
                    loading: true,
                    error: null,
                    data: null
                };
            })
            .addCase(generateReferralStrategy.fulfilled, (state, action) => {
                const { organizationId, strategy, source, companyName, jobPosition } = action.payload;
                state.referralStrategies[organizationId] = {
                    loading: false,
                    error: null,
                    data: strategy,
                    source: source,
                    companyName: companyName,
                    jobPosition: jobPosition
                };
            })
            .addCase(generateReferralStrategy.rejected, (state, action) => {
                const organizationId = action.meta.arg.organizationId;
                state.referralStrategies[organizationId] = {
                    loading: false,
                    error: action.payload,
                    data: null
                };
            })
    }
});

export const { 
    startRealtimeSearch, 
    setSearchComplete, 
    setCompanyLoading, 
    updateContactsForCompany, 
    setCompanyError,
    setCompanyAnalysisComplete,
    setPreAnalyzedData,
    clearCompanyPending,
    setDemoContacts,
    setReferralStrategy
} = contactsSlice.actions;
export default contactsSlice.reducer;
